import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProjects } from '../services/projectService';

const STATUS_COLOR = {
  draft:    { bg: '#1e293b', border: '#64748b', text: '#94a3b8' },
  active:   { bg: '#14532d', border: '#22c55e', text: '#86efac' },
  closed:   { bg: '#1e3a5f', border: '#3b82f6', text: '#7dd3fc' },
  archived: { bg: '#1c1917', border: '#78716c', text: '#a8a29e' },
};
const TYPE_COLOR = {
  public:    '#0ea5e9',
  community: '#a855f7',
  hybrid:    '#f59e0b',
};
const STATUS_LABEL = { draft: 'Draft', active: 'Active', closed: 'Closed', archived: 'Archived' };
const TYPE_LABEL   = { public: 'Public', community: 'Community', hybrid: 'Hybrid' };
const FILTERS = ['all', 'draft', 'active', 'closed', 'archived'];

function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.draft;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>{STATUS_LABEL[status] || status}</span>
  );
}

function TypeBadge({ type }) {
  const color = TYPE_COLOR[type] || '#94a3b8';
  return (
    <span style={{
      background: '#0f172a', border: `1px solid ${color}`, color,
      borderRadius: 5, padding: '2px 8px', fontSize: 11,
    }}>{TYPE_LABEL[type] || type}</span>
  );
}

function ProjectCard({ project }) {
  const community = project.tenantId?.branding?.displayName || project.tenantId?.name || '\u2014';
  const date = project.createdAt
    ? new Date(project.createdAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
    : '\u2014';
  return (
    <Link to={`/projects/${project._id}`} style={{ textDecoration: 'none' }}>
      <div
        style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
          padding: '16px 18px', cursor: 'pointer', transition: 'border-color 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', lineHeight: 1.3 }}>
            {project.title}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {project.geometry && (
              <span title="Has location footprint" style={{ fontSize: 13 }}>&#128205;</span>
            )}
            <StatusBadge status={project.status} />
          </div>
        </div>

        {(project.projectNumber || project.proponentOrg) && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 5 }}>
            {project.projectNumber && <span>#{project.projectNumber}</span>}
            {project.projectNumber && project.proponentOrg && <span> &middot; </span>}
            {project.proponentOrg && <span>{project.proponentOrg}</span>}
          </div>
        )}

        {project.description && (
          <div style={{
            fontSize: 12, color: '#94a3b8', marginTop: 8,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {project.description}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{
              background: '#0f172a', border: '1px solid #334155', borderRadius: 4,
              padding: '2px 7px', fontSize: 11, color: '#64748b',
            }}>{community}</span>
            <TypeBadge type={project.type} />
          </div>
          <span style={{ fontSize: 11, color: '#475569' }}>{date}</span>
        </div>
      </div>
    </Link>
  );
}

export default function ProponentPage() {
  const navigate = useNavigate();

  const [projects, setProjects]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [statusFilter, setStatusFilter]   = useState('all');

  useEffect(() => {
    getProjects({ mine: true })
      .then(r => setProjects(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const total       = projects.length;
  const activeCount = projects.filter(p => p.status === 'active').length;
  const draftCount  = projects.filter(p => p.status === 'draft').length;

  const filtered = statusFilter === 'all'
    ? projects
    : projects.filter(p => p.status === statusFilter);

  const tabStyle = (active) => ({
    background: active ? '#1e3a5f' : 'none',
    border: `1px solid ${active ? '#3b82f6' : '#334155'}`,
    color: active ? '#7dd3fc' : '#64748b',
    borderRadius: 6, padding: '5px 13px',
    cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
  });

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto', color: '#e2e8f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9' }}>My Projects</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
            Proponent Portal \u2014 manage your project submissions across all communities
          </div>
        </div>
        <button
          onClick={() => navigate('/proponent/new')}
          style={{
            background: '#1d4ed8', border: 'none', borderRadius: 7,
            color: '#fff', padding: '9px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
          }}
        >
          + New Project
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { num: total,       lbl: 'Total Projects', color: '#f1f5f9' },
          { num: activeCount, lbl: 'Active',          color: '#86efac' },
          { num: draftCount,  lbl: 'Draft',           color: '#94a3b8' },
        ].map(({ num, lbl, color }) => (
          <div key={lbl} style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
            padding: '16px 20px', flex: '1 1 160px',
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{num}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{lbl}</div>
          </div>
        ))}
        <Link to="/proponent/profile" style={{ textDecoration: 'none', flex: '1 1 160px' }}>
          <div style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
            padding: '16px 20px', cursor: 'pointer', transition: 'border-color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#c084fc'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}
          >
            <div style={{ fontSize: 28, fontWeight: 800, color: '#c084fc', lineHeight: 1 }}>&#128100;</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>My Profile</div>
          </div>
        </Link>
      </div>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#64748b', marginRight: 4 }}>Filter:</span>
          {FILTERS.map(f => (
            <button key={f} style={tabStyle(statusFilter === f)} onClick={() => setStatusFilter(f)}>
              {f === 'all' ? 'All' : STATUS_LABEL[f]}
              {f !== 'all' && (
                <span style={{ marginLeft: 5, opacity: 0.7 }}>
                  ({projects.filter(p => p.status === f).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: '#64748b', fontSize: 13, padding: '20px 0' }}>Loading projects\u2026</div>
        ) : filtered.length === 0 ? (
          <div style={{
            background: '#1e293b', border: '1px dashed #334155', borderRadius: 10,
            padding: '36px 20px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>&#128203;</div>
            <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 14 }}>
              {statusFilter === 'all'
                ? 'No projects yet. Create your first project to get started.'
                : `No ${STATUS_LABEL[statusFilter]?.toLowerCase()} projects.`}
            </div>
            {statusFilter === 'all' && (
              <button onClick={() => navigate('/proponent/new')} style={{
                background: '#1d4ed8', border: 'none', borderRadius: 6,
                color: '#fff', padding: '8px 18px', cursor: 'pointer', fontSize: 13,
              }}>
                + Create First Project
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14,
          }}>
            {filtered.map(p => <ProjectCard key={p._id} project={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
